import {Component, OnDestroy, OnInit} from '@angular/core';
import {forkJoin, Subject, takeUntil} from 'rxjs';
import {Router} from '@angular/router';
import {QueryModel} from '../../../services/http/http.service';
import {PatientFile, PatientFileModel} from '../../../models/patient-file/patient-file.model';
import {PatientModel} from '../../../models/patient/patient.model';
import {PatientService} from '../../../services/patient/patient.service';
import {iResponse, iResponseList} from '../../../models/response/response.model';
import {PatientFileHandlingComponent} from '../patient-file-handling/patient-file-handling.component';
import {DeviceDetectionService} from '../../../services/device-detection/device-detection.service';
import {MatTab, MatTabGroup} from '@angular/material/tabs';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {MatInput, MatSuffix} from '@angular/material/input';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {GenderService} from '../../../services/gender/gender.service';
import {GenderModel} from '../../../models/gender/gender.model';
import {MatOption, MatSelect} from '@angular/material/select';
import {DatePipe} from '@angular/common';
import {GenderPipe} from '../../../pipe/gender/gender.pipe';
import {IdDocumentModel} from '../../../models/registration/registration.model';
import {InfoMessageService} from '../../../services/infomessage/infomessage.service';
import {FileUploadComponent} from '../../file-upload/file-upload.component';
import {UploadService} from '../../../services/upload/upload.service';
import {DoctorModel} from '../../../models/doctor/doctor.model';
import {DoctorService} from '../../../services/doctor/doctor.service';
import {MatDialog} from '@angular/material/dialog';
import {FilePermissionModalComponent} from '../../modals/file-permission-modal/file-permission-modal.component';
import {PharmacyService} from '../../../services/pharmacy/pharmacy.service';
import {AuthenticationService} from '../../../services/authentication/authentication.service';
import {PharmacyModel} from '../../../models/pharmacy/pharmacy.model';
import {AddressModel} from '../../../models/registration/registration.model';
import {ChangePasswordModel} from '../../../models/change-password/change-password.model';

@Component({
    selector: 'app-patient-file',
    imports: [
        PatientFileHandlingComponent,
        MatTabGroup,
        MatTab,
        MatDatepicker,
        MatDatepickerInput,
        MatDatepickerToggle,
        MatInput,
        MatSuffix,
        ReactiveFormsModule,
        MatSelect,
        MatOption,
        GenderPipe,
        FileUploadComponent,
        DatePipe
    ],
    templateUrl: './patient-file.component.html',
    styleUrl: './patient-file.component.sass',
    standalone: true
})
export class PatientFileComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    query!: QueryModel;

    patientFile!: PatientFileModel;
    patient!: PatientModel;
    forFollowingPrescription: boolean = false
    isMobile: boolean = false
    genders: GenderModel[] = [];
    idFiles: PatientFile[] = [];
    doctorList: DoctorModel[] = [];
    openPermissionsId: string = '';
    showUploadFields: boolean = false;
    isLoadingIdFiles: boolean = false;
    pharmacyList: PharmacyModel[] = [];

    idDocuments: IdDocumentModel = {
        front: {
            fileData: '',
            fileName: '',
            mimeType: ''
        },
        back: {
            fileData: '',
            fileName: '',
            mimeType: ''
        }
    }


    profileForm: FormGroup = new FormGroup({
        email: new FormControl(''),
        firstName: new FormControl(''),
        id: new FormControl(''),
        lastName: new FormControl(''),
        mobileNumber: new FormControl(''),
        phoneNumber: new FormControl(''),
        skype: new FormControl(''),
        genderId: new FormControl(''),
        dateOfBirth: new FormControl(''),
    })

    deliveryAddressForm: FormGroup = new FormGroup({
        city: new FormControl(''),
        houseNumber: new FormControl(''),
        id: new FormControl(''),
        street: new FormControl(''),
        zipCode: new FormControl(''),
    })

    invoiceAddressForm: FormGroup = new FormGroup({
        city: new FormControl(''),
        houseNumber: new FormControl(''),
        id: new FormControl(''),
        street: new FormControl(''),
        zipCode: new FormControl(''),
    })

    passwordForm: FormGroup = new FormGroup({
        newPassword: new FormControl(''),
        passwordConfirm: new FormControl(''),
    })

    pharmacyIdFormControl: FormControl = new FormControl('')
    customPharmacyForm = new FormGroup({
        name: new FormControl('', Validators.required),
        street: new FormControl('', Validators.required),
        houseNumber: new FormControl('', Validators.required),
        zipCode: new FormControl('', Validators.required),
        city: new FormControl('', Validators.required),
        phoneNumber: new FormControl('', Validators.required),
    })


    constructor(
        private patientService: PatientService,
        private router: Router,
        private deviceDetectionService: DeviceDetectionService,
        private genderService: GenderService,
        private infoMessageService: InfoMessageService,
        private uploadService: UploadService,
        private doctorService: DoctorService,
        private dialog: MatDialog,
        private pharmacyService: PharmacyService,
        private authenticationService: AuthenticationService,
    ) {
        const navigation = this.router.currentNavigation();
        const forFollowingPrescription = navigation?.extras?.state?.['forFollowingPrescription'];
        if (forFollowingPrescription) {
            this.forFollowingPrescription = forFollowingPrescription
        }
        this.isMobile = deviceDetectionService.getIsMobile();
    }

    ngOnInit() {
        this.getMe();
        this.getGenders();
        this.getDoctors();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toProfile(): void {
        this.router.navigate(['profil']);
    }


    private getPatientFiles(profileId: string): void {
        this.patientService.getPatientFiles(profileId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<PatientFileModel>) => {
                    this.patientFile = response.data[0];
                },
                error: (error) => {
                    console.error(error)
                }
            });
    }

    private getMe(): void {
        this.patientService.getMe()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponse<PatientModel>) => {
                    this.patient = response.data;
                    console.log('patient', this.patient)
                    this.parseProfileToForm(this.patient);
                    this.getPatientFiles(this.patient.id);
                    this.getIdFiles(this.patient.id, 'id-documents');
                },
                error: (error) => {
                    console.error(error)
                }
            })
    }

    private parseProfileToForm(patient: PatientModel): void {
        this.profileForm.patchValue(patient);
        if (patient.gender) {
            this.profileForm.get('genderId')?.setValue(patient.gender.id);
        }
        if (patient.deliveryAddress) {
            this.deliveryAddressForm.patchValue(patient.deliveryAddress);
        }
        if (patient.invoiceAddress) {
            this.invoiceAddressForm.patchValue(patient.invoiceAddress);
        }
        this.getPharmacyList();
    }

    private getGenders(): void {
        this.genderService.getGenders()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<GenderModel>) => {
                    this.genders = response.data;
                }
            })
    }

    onSubmitProfile(): void {
        if (this.profileForm.invalid && this.deliveryAddressForm.invalid && this.invoiceAddressForm.invalid) return;

        const updatedPatient: PatientModel = {
            ...this.patient,
            ...this.profileForm.value,
            deliveryAddress: this.deliveryAddressForm.value,
            invoiceAddress: this.invoiceAddressForm.value,
        };

        this.patientService.updateMe(updatedPatient)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponse<PatientModel>) => {
                    this.patient = response.data;
                    this.infoMessageService.createMessage('Profil erfolgreich aktualisiert');
                },
                error: () => {
                    this.infoMessageService.createMessage('Fehler beim Aktualisieren des Profils', 'error');
                }
            });
    }

    onSubmitPharmacy(): void {
        if (this.customPharmacyForm.invalid && this.pharmacyIdFormControl.value === 'custom') {
            this.customPharmacyForm.markAllAsTouched();
            return;
        }

        const updatedPatient: PatientModel = {
            ...this.patient
        };

        const pharmacy: PharmacyModel = {
            address: {city: "", houseNumber: "", street: "", zipCode: ""},
            email: '', id: '', isDefault: false, name: '', noSalesTaxes: false, phoneNumber: '', serialNumber: 0
        };

        if (this.pharmacyIdFormControl.value === '' || this.pharmacyIdFormControl.value === 'none') {
            updatedPatient.regularPharmacy = null;
        } else {
            if (this.pharmacyIdFormControl.value === 'custom') {
                pharmacy.id = '00000000-0000-0000-0000-000000000000';
                pharmacy.name = <string>this.customPharmacyForm.get('name')?.value;
                pharmacy.address = <AddressModel>this.customPharmacyForm.value;
                pharmacy.phoneNumber = <string>this.customPharmacyForm.get('phoneNumber')?.value;
            } else {
                const found = this.pharmacyList.find(p => p.id === this.pharmacyIdFormControl.value);
                if (found) {
                    pharmacy.id = found.id;
                    pharmacy.name = found.name;
                    pharmacy.address = found.address;
                    pharmacy.phoneNumber = found.phoneNumber;
                }
            }
            updatedPatient.regularPharmacy = pharmacy;
        }

        this.patientService.updateMe(updatedPatient)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponse<PatientModel>) => {
                    this.patient = response.data;
                    this.infoMessageService.createMessage('Standard Apotheke erfolgreich aktualisiert');
                },
                error: () => {
                    this.infoMessageService.createMessage('Fehler beim Aktualisieren der Apotheke', 'error');
                }
            });
    }

    onSubmitPassword(): void {
        if (this.passwordForm.invalid) return;
        if (this.passwordForm.get('newPassword')?.value !== this.passwordForm.get('passwordConfirm')?.value) {
            this.infoMessageService.createMessage('Passwörter stimmen nicht überein', 'error');
            return;
        }

        const changePasswordModel: ChangePasswordModel = {
            newPassword: this.passwordForm.get('newPassword')?.value
        };

        this.authenticationService.changePassword(changePasswordModel)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.passwordForm.reset();
                    this.infoMessageService.createMessage('Passwort erfolgreich aktualisiert');
                },
                error: () => {
                    this.infoMessageService.createMessage('Fehler beim Aktualisieren des Passworts', 'error');
                }
            });
    }

    checkPharmacyDisabledState(): void {
        if (this.pharmacyIdFormControl.value !== 'custom') {
            this.customPharmacyForm.disable();
            this.compareRegularPharmacy();
        } else {
            this.customPharmacyForm.reset();
            this.customPharmacyForm.enable();
        }
    }

    private getPharmacyList() {
        this.pharmacyService.getPharmacies()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<PharmacyModel>) => {
                    this.pharmacyList = response.data;

                    if (this.patient?.regularPharmacy && this.patient.regularPharmacy.id) {
                        if (this.patient.regularPharmacy.id === '00000000-0000-0000-0000-000000000000') {
                            this.pharmacyIdFormControl.patchValue('custom');
                            this.customPharmacyForm.patchValue(this.patient.regularPharmacy.address);
                            this.customPharmacyForm.get('name')?.patchValue(this.patient.regularPharmacy.name);
                            this.customPharmacyForm.get('phoneNumber')?.patchValue(this.patient.regularPharmacy.phoneNumber);
                        } else {
                            this.pharmacyIdFormControl.patchValue(this.patient.regularPharmacy.id);
                            this.compareRegularPharmacy();
                        }
                    }
                }
            });
    }

    private compareRegularPharmacy(): void {
        const found = this.pharmacyList.find(p => p.id === this.pharmacyIdFormControl.value);
        if (found) {
            this.customPharmacyForm.patchValue(found.address);
            this.customPharmacyForm.get('name')?.patchValue(found.name);
            this.customPharmacyForm.disable();
        }
    }

    onFileSelectedFront(file: any): void {
        this.idDocuments.front.fileData = file.fileData;
        this.idDocuments.front.fileName = file.fileName;
        this.idDocuments.front.mimeType = file.mimeType;
    }

    onFileSelectedBack(file: any): void {
        this.idDocuments.back.fileData = file.fileData;
        this.idDocuments.back.fileName = file.fileName;
        this.idDocuments.back.mimeType = file.mimeType;
    }

    removeFile(type: 'front' | 'back'): void {
        if (type === 'front') {
            this.idDocuments.front = {fileData: '', fileName: '', mimeType: ''};
        } else {
            this.idDocuments.back = {fileData: '', fileName: '', mimeType: ''};
        }
    }

    onSubmitIdDocuments(): void {
        if (this.idDocuments.front.fileData === '' || this.idDocuments.back.fileData === '') {
            this.infoMessageService.createMessage('Bitte beide Seiten des Ausweises hochladen', 'error');
            return;
        }

        // Convert base64 to File objects
        const frontFile = this.base64ToFile(this.idDocuments.front.fileData, this.idDocuments.front.fileName, this.idDocuments.front.mimeType);
        const backFile = this.base64ToFile(this.idDocuments.back.fileData, this.idDocuments.back.fileName, this.idDocuments.back.mimeType);

        this.uploadService.uploadIDFiles(frontFile, backFile, this.patient.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.infoMessageService.createMessage('Ausweisdokumente erfolgreich hochgeladen');
                    this.idDocuments.front = {fileData: '', fileName: '', mimeType: ''};
                    this.idDocuments.back = {fileData: '', fileName: '', mimeType: ''};
                    this.getIdFiles(this.patient.id, 'id-documents');
                },
                error: () => {
                    this.infoMessageService.createMessage('Fehler beim Hochladen der Ausweisdokumente', 'error');
                }
            });
    }

    private getIdFiles(patientId: string, documentTypeId: string): void {
        this.isLoadingIdFiles = true;
        const query: QueryModel = {
            userId: patientId,
            documentType: documentTypeId,
            base64: true
        };
        this.uploadService.getFiles(query)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<PatientFile>) => {
                    this.idFiles = response.data;
                    this.showUploadFields = this.idFiles.length === 0;
                    if (this.idFiles.length > 0) {
                        this.getBase64FilesForIdFiles(patientId);
                    } else {
                        this.isLoadingIdFiles = false;
                    }
                },
                error: (error) => {
                    console.error(error);
                    this.isLoadingIdFiles = false;
                }
            });
    }

    private getBase64FilesForIdFiles(patientId: string): void {
        const queries = this.idFiles.map(file => {
            const query: QueryModel = {
                userId: patientId,
                base64: true
            };
            return this.uploadService.getFile(file.id, query)
                .pipe(takeUntil(this.destroy$));
        });

        if (queries.length === 0) {
            this.isLoadingIdFiles = false;
            return;
        }

        forkJoin(queries).subscribe({
            next: (responses: any[]) => {
                responses.forEach((response, index) => {
                    this.idFiles[index].base46 = response.data;
                });
                this.isLoadingIdFiles = false;
            },
            error: (error) => {
                console.error(error);
                this.isLoadingIdFiles = false;
            }
        });
    }

    private getDoctors(): void {
        this.doctorService.getDoctors()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<DoctorModel>) => {
                    this.doctorList = response.data;
                },
                error: (error) => {
                    console.error(error)
                }
            })
    }

    openPermissions(fileId: string): void {
        if (this.openPermissionsId === fileId) {
            this.openPermissionsId = '';
        } else {
            this.openPermissionsId = fileId;
        }
    }

    toggleUpload(show: boolean): void {
        this.showUploadFields = show;
    }

    deleteIdFile(fileId: string): void {
        this.uploadService.deleteFile(fileId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.infoMessageService.createMessage('Dokument erfolgreich gelöscht');
                    this.getIdFiles(this.patient.id, 'id-documents');
                },
                error: () => {
                    this.infoMessageService.createMessage('Fehler beim Löschen des Dokuments', 'error');
                }
            });
    }

    triggerDownload(file: PatientFile): void {
        this.uploadService.downloadFile(file.id, file.fileName, file.contentType)
    }

    deletePermission(id: string): void {
        const fileId = this.idFiles.find(file => file.permissionsGranted.some(p => p.id === id))?.id;
        if (!fileId) return;

        this.uploadService.deleteFilePermissions(fileId, id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.infoMessageService.createMessage('Berechtigung erfolgreich entzogen');
                    this.getIdFiles(this.patient.id, 'id-documents');
                },
                error: () => {
                    this.infoMessageService.createMessage('Fehler beim Entziehen der Berechtigung', 'error');
                }
            });
    }

    openPermissionModal(file: PatientFile, fileName: string = ''): void {
        const dialogRef = this.dialog.open(FilePermissionModalComponent, {
            data: {
                file: file,
                fileName: fileName
            }
        });
        dialogRef.afterClosed().subscribe(() => {
            this.getIdFiles(this.patient.id, 'id-documents');
        });
    }

    private base64ToFile(base64: string, fileName: string, mimeType: string): File {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new File([byteArray], fileName, {type: mimeType});
    }
}
